import { useCallback, useEffect, useMemo, useState } from 'react'
import { useFirestoreUsers } from '../../firebase/useFirestore'
import PageHeader from '../../components/layout/PageHeader.jsx'
import Table from '../../components/common/table/Table.jsx'
import TableCell from '../../components/common/table/TableCell.jsx'
import ReadOnlyGuard from '../../components/common/ReadOnlyGuard'
import Modal from '../../components/common/Modal'
import getSubmitterName from '../../helpers/getSubmitterName'
import restoreDeletedItem from '../../helpers/restoreDeletedItem'
import fetchTrashItems from '../../helpers/trash/fetchTrashItems'
import { collectionLabel } from '../../helpers/trash/trashCollections'

/**
 * De prullenbak: alles wat in deze omgeving is verwijderd, met de mogelijkheid
 * het terug te zetten.
 *
 * Loopt de collecties zelf af in plaats van een eigen registratie bij te
 * houden: verwijderen gebeurt op ruim dertig plekken in de code en lang niet
 * allemaal via `softDeleteDoc`, dus een registratie zou alleen tonen wat
 * toevallig langs die ene helper kwam.
 * AI-CONTEXT: See docs/plans/prullenbak.md
 *
 * @returns {JSX.Element}
 */
const Prullenbak = () => {
    /** @type {[Array<object>, Function]} */
    const [items, setItems] = useState([])
    const [loading, setLoading] = useState(true)
    const [fout, setFout] = useState('')
    const [bezig, setBezig] = useState('')
    const [melding, setMelding] = useState('')
    const [herstelItem, setHerstelItem] = useState(null)
    /** @type {[Array<string>, Function]} */
    const [afgekapt, setAfgekapt] = useState([])

    const users = useFirestoreUsers()

    // Eenmalig ophalen in plaats van een live abonnement: de prullenbak
    // verandert langzaam, en na een herstelactie wordt hij zelf ververst.
    const ophalen = useCallback(async () => {
        setLoading(true)
        setFout('')
        try {
            const { items: rijen, mislukt, afgekapt: vol } = await fetchTrashItems()

            setItems(rijen)
            setAfgekapt(vol)

            // Eén onleesbare collectie is geen reden om de hele pagina leeg te
            // laten, maar wel om het te zeggen — anders lijkt het alsof er
            // niets verwijderd is.
            if (mislukt.length) {
                setFout(`Van ${mislukt.length} soort(en) kon de prullenbak niet worden gelezen: ${mislukt.join(', ')}.`)
            }
        } catch (err) {
            console.error('Prullenbak kon niet worden opgehaald:', err)
            setFout('De prullenbak kon niet worden opgehaald. Probeer het opnieuw.')
        } finally {
            setLoading(false)
        }
    }, [])

    useEffect(() => { ophalen() }, [ophalen])

    const datum = (waarde) => {
        if (!waarde?.toDate) return ''
        return waarde.toDate().toLocaleDateString('nl-NL', {
            day: 'numeric', month: 'long', year: 'numeric',
            hour: '2-digit', minute: '2-digit',
        })
    }

    const herstel = async () => {
        if (!herstelItem) return
        setBezig(herstelItem.DocID)
        setMelding('')
        try {
            await restoreDeletedItem(herstelItem)
            setMelding(`${collectionLabel(herstelItem.Collection)} "${herstelItem.Title}" is teruggezet.`)
            await ophalen()
        } catch (err) {
            console.error('Terugzetten mislukt:', err)
            setMelding('Terugzetten is mislukt. Probeer het opnieuw.')
        } finally {
            setBezig('')
            setHerstelItem(null)
        }
    }

    const columns = useMemo(() => ([
        { key: 'wat', label: 'WAT' },
        { key: 'soort', label: 'SOORT' },
        { key: 'wanneer', label: 'WANNEER', searchable: false },
        { key: 'wie', label: 'DOOR', searchable: false },
        { key: 'terug', label: 'TERUGZETTEN', searchable: false },
    ]), [])

    return (
        <div className="main">
            <div className="main-container">
                <PageHeader
                    heading={'Prullenbak'}
                    description={'Hier staat alles wat in deze omgeving is verwijderd. Je kunt het terugzetten op de plek waar het vandaan komt.'}
                    AI={false}
                />

                <div className="table-container dashboard-container">
                    {fout && <p className="prullenbak-fout" role="alert">{fout}</p>}
                    {melding && <p className="prullenbak-melding" role="status">{melding}</p>}
                    {afgekapt.length > 0 && (
                        <p className="prullenbak-melding" role="status">
                            Van {afgekapt.join(', ')} worden alleen de eerste 300 getoond.
                        </p>
                    )}

                    {loading ? (
                        <p>Gegevens laden…</p>
                    ) : (
                        <ReadOnlyGuard edit="settings.edit">
                            {(canEdit) => (
                                <Table
                                    columns={columns}
                                    items={items}
                                    getItemKey={(item) => `${item.Collection}-${item.DocID}`}
                                    renderRow={(item) => (
                                        <>
                                            <TableCell><p>{item.Title}</p></TableCell>
                                            <TableCell><p>{collectionLabel(item.Collection)}</p></TableCell>
                                            <TableCell><p>{datum(item.DeletedAt)}</p></TableCell>
                                            <TableCell>
                                                <p>{item.DeletedBy ? getSubmitterName(item.DeletedBy, users) : 'Onbekend'}</p>
                                            </TableCell>
                                            <TableCell>
                                                {canEdit && (
                                                    <button
                                                        type="button"
                                                        className="prullenbak-herstel-knop"
                                                        disabled={bezig === item.DocID}
                                                        onClick={() => setHerstelItem(item)}
                                                    >
                                                        {bezig === item.DocID ? 'Bezig…' : 'Terugzetten'}
                                                    </button>
                                                )}
                                            </TableCell>
                                        </>
                                    )}
                                    searchAccessor={(item) =>
                                        `${item?.Title || ''} ${collectionLabel(item?.Collection)}`
                                    }
                                    searchPlaceholder="Zoeken in de prullenbak"
                                    emptyMessage="De prullenbak is leeg."
                                />
                            )}
                        </ReadOnlyGuard>
                    )}
                </div>

                <Modal
                    modalOpen={!!herstelItem}
                    setModalOpen={() => setHerstelItem(null)}
                    pageHeader={'Terugzetten'}
                    confirm={herstel}
                    confirmLabel="Terugzetten"
                    content={
                        <div className="prullenbak-herstel-venster">
                            <p>
                                Weet je zeker dat je <b>{herstelItem?.Title}</b> wilt terugzetten?
                            </p>
                            <p>
                                Het onderdeel komt terug op de plek waar het vandaan kwam. Wat er
                                aan hing — bijvoorbeeld de notities bij een huishouden — staat als
                                eigen regel in deze lijst en kun je apart terugzetten.
                            </p>
                        </div>
                    }
                />
            </div>
        </div>
    )
}

export default Prullenbak
